// The room's URL-backed state: channel, time range, five cross-filter
// dimensions, the active chapter and the parts sub-view — all in the query
// string so every view is shareable and nothing new touches client storage.

import { useMemo } from "react"
import { useSearchParams } from "react-router"
import { useSearchParamState } from "@/lib/utils/use-search-param-state"
import { RANGES, SOURCES, type DimFilters, type RangeKey, type SourceKey } from "./room-model"

export function useRoomState(opts?: { defaultChapter?: string }) {
  const [source, setSource] = useSearchParamState("source", "all")
  const [range, setRange] = useSearchParamState("range", "30d")
  const [advisorId, setAdvisorId] = useSearchParamState("advisor", "")
  const [storeId, setStoreId] = useSearchParamState("store", "")
  const [tier, setTier] = useSearchParamState("tier", "")
  const [section, setSection] = useSearchParamState("section", "")
  const [make, setMake] = useSearchParamState("make", "")
  const [chapter, setChapter] = useSearchParamState("chapter", opts?.defaultChapter ?? "lane")
  const [parts, setParts] = useSearchParamState("parts", "section")
  const [usage, setUsage] = useSearchParamState("usage", "wtd")
  const [, setSearchParamsRaw] = useSearchParams()

  const rangeDef = RANGES[(range as RangeKey) in RANGES ? (range as RangeKey) : "30d"]

  const f: DimFilters = useMemo(() => ({
    source: SOURCES.some(s => s.key === source) ? (source as SourceKey) : null,
    advisorId, storeId, tier, section, make,
  }), [source, advisorId, storeId, tier, section, make])

  const toggleAdvisor = (id: string) => setAdvisorId(advisorId === id ? "" : id)
  const toggleStore = (id: string) => setStoreId(storeId === id ? "" : id)
  const toggleTier = (idx: number) => setTier(tier === String(idx) ? "" : String(idx))
  const toggleSection = (key: string) => setSection(section === key ? "" : key)
  const toggleMake = (m: string) => setMake(make === m ? "" : m)

  // One atomic update — individual setters computed from pre-batch params
  // would lose all but the last write. Source is a chip now, so it clears too.
  const clearDims = () => setSearchParamsRaw(prev => {
    const next = new URLSearchParams(prev)
    ;["source", "advisor", "store", "tier", "section", "make"].forEach(k => next.delete(k))
    return next
  }, { replace: true })

  // Atomic multi-key apply — a signal card CTA jumps to a chapter AND sets
  // dimensional filters in a single navigation (same discipline as clearDims).
  const applyScope = (updates: Record<string, string | null | undefined>) => setSearchParamsRaw(prev => {
    const next = new URLSearchParams(prev)
    for (const [k, v] of Object.entries(updates)) {
      if (v == null || v === "") next.delete(k); else next.set(k, v)
    }
    return next
  }, { replace: true })

  return {
    source, setSource, range, setRange, rangeDef,
    advisorId, setAdvisorId, storeId, setStoreId, tier, setTier, section, setSection, make, setMake,
    chapter, setChapter, parts, setParts, usage, setUsage,
    f, toggleAdvisor, toggleStore, toggleTier, toggleSection, toggleMake, clearDims, applyScope,
  }
}
export type RoomState = ReturnType<typeof useRoomState>
